import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  ProjectContainer,
  ProjectTitle,
  Tagline,
  MetricsStrip,
  StatCard,
  ProjectDescription,
  ProjectInfo,
  SectionTitle,
  CompareGrid,
  CompareCard,
  Footnote,
  ProjectWrapper,
  Chips,
  Divider,
  VideoEmbed,
} from "./styles";

interface ProjectAiAssistantProps {
}

const ProjectAiAssistant: React.FC<ProjectAiAssistantProps> = () => {
  const { t } = useLanguage();

  return (
    <ProjectContainer>
      <ProjectTitle>
        <h2>{t("aiassistant.title")}</h2>
        <p className="tags">{t("aiassistant.role")}</p>
      </ProjectTitle>

      <Tagline>{t("aiassistant.tagline")}</Tagline>

      <MetricsStrip>
        <StatCard>
          <span className="value">{t("aiassistant.statCost")}</span>
          <span className="label">{t("aiassistant.statCostLabel")}</span>
        </StatCard>
        <StatCard>
          <span className="value">{t("aiassistant.statContext")}</span>
          <span className="label">{t("aiassistant.statContextLabel")}</span>
        </StatCard>
        <StatCard>
          <span className="value">{t("aiassistant.statLines")}</span>
          <span className="label">{t("aiassistant.statLinesLabel")}</span>
        </StatCard>
        <StatCard>
          <span className="value">{t("aiassistant.statVoice")}</span>
          <span className="label">{t("aiassistant.statVoiceLabel")}</span>
        </StatCard>
      </MetricsStrip>

      <ProjectDescription>{t("aiassistant.overview")}</ProjectDescription>

      <ProjectInfo>
        <p>
          <b>{t("aiassistant.roleLabel")}:</b> {t("aiassistant.myRole")}
        </p>
        <p>
          <b>{t("aiassistant.stackLabel")}:</b> {t("aiassistant.stack")}
        </p>
        <p>
          <b>{t("aiassistant.githubLabel")}:</b>{" "}
          <a
            href="https://github.com/YuanyuanMaggie/ragchatbot"
            target="_blank"
            rel="noopener noreferrer"
          >
            github.com/YuanyuanMaggie/ragchatbot
          </a>
        </p>
        <p>{t("aiassistant.tryItLive")}</p>
      </ProjectInfo>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t("aiassistant.decisionTitle")}</SectionTitle>
        <CompareGrid>
          <CompareCard>
            <p className="head">{t("aiassistant.ragHead")}</p>
            <ul>
              <li>{t("aiassistant.ragPoint1")}</li>
              <li>{t("aiassistant.ragPoint2")}</li>
              <li>{t("aiassistant.ragPoint3")}</li>
            </ul>
            <span className="verdict">{t("aiassistant.ragVerdict")}</span>
          </CompareCard>
          <CompareCard $primary>
            <p className="head">{t("aiassistant.cagHead")}</p>
            <ul>
              <li>{t("aiassistant.cagPoint1")}</li>
              <li>{t("aiassistant.cagPoint2")}</li>
              <li>{t("aiassistant.cagPoint3")}</li>
            </ul>
            <span className="verdict">{t("aiassistant.cagVerdict")}</span>
          </CompareCard>
        </CompareGrid>
        <Footnote>{t("aiassistant.decisionIntro")}</Footnote>
      </ProjectWrapper>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t("aiassistant.archTitle")}</SectionTitle>
        <ul>
          <li>{t("aiassistant.archApp")}</li>
          <li>{t("aiassistant.archAi")}</li>
          <li>{t("aiassistant.archSession")}</li>
          <li>{t("aiassistant.archServices")}</li>
        </ul>
        <img loading="lazy" decoding="async"
          src="/images/projects/aiassistant-architecture.webp"
          alt="AI Profile Assistant architecture"
        />
      </ProjectWrapper>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t("aiassistant.featuresTitle")}</SectionTitle>
        <ul>
          <li>{t("aiassistant.featuresGrounded")}</li>
          <li>{t("aiassistant.featuresMultiturn")}</li>
          <li>{t("aiassistant.featuresVoice")}</li>
          <li>{t("aiassistant.featuresRealtime")}</li>
          <li>{t("aiassistant.featuresCost")}</li>
        </ul>
        <VideoEmbed>
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/BLzqI1N--sY?si=qrf2L_S-CyV6NaW9"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </VideoEmbed>
      </ProjectWrapper>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t("aiassistant.highlightsTitle")}</SectionTitle>
        <ul>
          <li>{t("aiassistant.highlightsRightsized")}</li>
          <li>{t("aiassistant.highlightsCaching")}</li>
          <li>{t("aiassistant.highlightsDegradation")}</li>
          <li>{t("aiassistant.highlightsPrivacy")}</li>
          <li>{t("aiassistant.highlightsIac")}</li>
          <li>{t("aiassistant.highlightsSecrets")}</li>
        </ul>
      </ProjectWrapper>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t("aiassistant.deployTitle")}</SectionTitle>
        <Chips>
          <span>{t("aiassistant.deployChip1")}</span>
          <span>{t("aiassistant.deployChip2")}</span>
          <span>{t("aiassistant.deployChip3")}</span>
          <span>{t("aiassistant.deployChip4")}</span>
          <span>{t("aiassistant.deployChip5")}</span>
          <span>{t("aiassistant.deployChip6")}</span>
        </Chips>
      </ProjectWrapper>

      <Divider />

      <ProjectInfo>
        <p>
          <b>{t("aiassistant.dateLabel")}:</b> {t("aiassistant.date")}
        </p>
      </ProjectInfo>
    </ProjectContainer>
  );
};

export default ProjectAiAssistant;
